import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { customFetch } from "../utils/custom-fetch";

const POLL_INTERVAL    = 20000;           // 20 שניות בין בדיקות
const TOKEN_REFRESH_MS = 60 * 60 * 1000; // רענון טוקן כל שעה

/* ---------- loader ---------- */
export const loader = async ({ request }) => {
  const url         = new URL(request.url);
  const screenId    = url.searchParams.get("screenId");
  const sessionToken =
    url.searchParams.get("sessionToken") ||
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("sessionToken"))
      ?.split("=")[1];

  if (screenId && sessionToken)
    return { redirectTo: `/public/${screenId}` };

  try {
    const { data } = await customFetch.get("/public/connect");
    return {
      qrCodeUrl:    data.qrCodeUrl,
      screenId:     data.screenId,
      sessionToken: data.sessionToken,
    };
  } catch {
    return { qrCodeUrl: null, error: true };
  }
};

/* ---------- קומפוננטה ---------- */
const ConnectPage = () => {
  const loaderData = useLoaderData();
  const navigate   = useNavigate();

  // qrCodeUrl is state so the image re-renders when the token refreshes
  const [qrCodeUrl, setQrCodeUrl] = useState(loaderData.qrCodeUrl);

  // screenId / sessionToken are refs — the interval always reads the latest
  // value without needing to restart itself
  const screenIdRef     = useRef(loaderData.screenId);
  const sessionTokenRef = useRef(loaderData.sessionToken);

  // Redirect immediately if already connected
  useEffect(() => {
    if (loaderData.redirectTo) navigate(loaderData.redirectTo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch a fresh token + QR code every hour
  const refreshToken = useCallback(async () => {
    try {
      const { data } = await customFetch.get("/public/connect");
      setQrCodeUrl(data.qrCodeUrl);
      screenIdRef.current     = data.screenId;
      sessionTokenRef.current = data.sessionToken;
    } catch (err) {
      console.error("[Connect] Error refreshing token:", err);
    }
  }, []);

  // Poll indefinitely until the user selects a building via the QR
  useEffect(() => {
    if (loaderData.redirectTo)                              return;
    if (!screenIdRef.current || !sessionTokenRef.current)  return;

    const pollId = setInterval(async () => {
      try {
        const res = await customFetch.get(
          `/public/get-selected-building/${screenIdRef.current}/${sessionTokenRef.current}`
        );
        if (res.status !== 404 && res.data?.buildingId) {
          clearInterval(pollId);
          clearInterval(tokenId); // eslint-disable-line no-use-before-define
          navigate(`/public/${res.data.buildingId}`);
        }
      } catch (err) {
        console.error("[Connect] Error checking building:", err);
      }
    }, POLL_INTERVAL);

    const tokenId = setInterval(refreshToken, TOKEN_REFRESH_MS);

    return () => {
      clearInterval(pollId);
      clearInterval(tokenId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loaderData.error)
    return <p>Error loading QR Code. Please try again later.</p>;

  return (
    <RobotContainer>
      <RobotImage
        src='https://res.cloudinary.com/dlhfcdscf/image/upload/v1785141722/public/qr_rgqdpz.jpg'
        alt='Robot'
      />
      {qrCodeUrl ? (
        <QrCodeImage src={qrCodeUrl} alt='QR Code' />
      ) : (
        <LoadingText>Loading QR Code...</LoadingText>
      )}

      <CountdownLabel>ממתין לחיבור...</CountdownLabel>
    </RobotContainer>
  );
};

/* ---------- styled-components ---------- */
const RobotContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  overflow: hidden;
`;

const RobotImage = styled.img`
  width: 100vw;
  height: auto;
`;

const QrCodeImage = styled.img`
  position: absolute;
  top: 62%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 450px;
  height: 450px;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: gray;
`;

const CountdownLabel = styled.div`
  position: absolute;
  top: 2%;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 18px;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 12px;
  border: 2px solid #00c8ff;
  font-size: 36px;
  font-weight: 700;
  color: #00c8ff;
  text-shadow:
    0 0 2px  #00c8ff,
    0 0 4px  #00c8ff,
    0 0 6px  #008cff;
  animation: neonPulse 2s ease-in-out infinite alternate;

  @keyframes neonPulse {
    from { box-shadow: 0 0 4px #008cff; }
    to   { box-shadow: 0 0 6px #00c8ff; }
  }
`;

export default ConnectPage;
